// ── Team Access Context (ClientApp/src/TeamAccessContext.tsx) ──
// Team Backlog and Manage Team are membership-driven, not role-driven: you reach them by
// being a triager, owner, or delegate on a registered team (or a work-item admin). Roles
// alone therefore cannot decide whether to render those left-nav entries, so this context
// asks the server once per session and caches the answer for the nav to gate on.

import React from 'react';
import { getMyTeamAccess, type CatalogueGrant, type MyTeamAccess } from './api/intake-v1';
import { getAccessIdentityKey, useAuth } from './AuthContext';

export interface TeamAccess extends MyTeamAccess {
  /** False until the first successful lookup lands; callers gate on the flags, not on this. */
  resolved: boolean;
}

interface TeamAccessContextValue extends TeamAccess {
  refresh: () => void;
  /** True when either answer is `unknown`, i.e. the nav is showing an unverified affordance. */
  degraded: boolean;
}

/**
 * The state before — or instead of — an answer.
 *
 * <p>`denied` rather than `unknown`, and that distinction is the whole point of this file. A
 * request that FAILED tells us nothing at all, so hiding is the safe default: the majority of
 * users own no team, and a triager who hits a blip still has the direct URLs plus the retry and
 * focus-refresh below to recover without a reload.</p>
 *
 * <p>A request that SUCCEEDED with `unknown` is the opposite situation — the server is explicitly
 * saying it could not confirm, which means the user may well have access. That case renders the
 * nav entry; see `isVisible`.</p>
 */
const NO_ACCESS: TeamAccess = { canTriage: 'denied', canManage: 'denied', resolved: false };

/**
 * Whether a nav entry should render.
 *
 * <p>`unknown` renders. Hiding an entry the user needs explains nothing and produces an access
 * request for access they already hold, whereas showing one that turns out to 503 costs a click
 * and the 503 explains itself ("this is not a permissions problem — please retry").</p>
 *
 * <p>The accepted cost: both answers are computed over the whole team catalogue, so during a FORGE
 * outage every signed-in user sees `unknown` — including the majority who own no team and will
 * see an entry appear that they have never had. That is deliberate. The population harmed by
 * showing loses a few seconds of confusion; the population harmed by hiding is the owners and
 * triagers with work to do, whose console would go dark for the duration of the outage.</p>
 *
 * <p><b>`canTriage` is NOT exempt from that cost.</b> An earlier version of this comment claimed it
 * was — that it is membership-scoped and so resolves to `denied` for a user with no teams even
 * during an outage. That was wrong, and the error mattered: `ListTriageCandidatesAsync`
 * deliberately over-returns every FORGE-anchored active team to every caller, because the roles
 * live in FORGE and Cosmos cannot filter on them. The membership cut happens afterwards, against
 * live verdicts. So one unresolvable team makes the whole answer `unknown` for everyone. See
 * `docs/incidents/2026-09-25-forge-dead-anchor-nav-fail-open.md` for the prod case where a single
 * dead anchor did exactly that.</p>
 *
 * <p>This is a genuine fail-open, so keep it for entries that lead to a self-explanatory 503 and
 * use a strict `=== 'granted'` test for anything that does not.</p>
 */
export const isVisible = (grant: CatalogueGrant): boolean => grant !== 'denied';

const TeamAccessContext = React.createContext<TeamAccessContextValue>({
  ...NO_ACCESS,
  degraded: false,
  refresh: () => {},
});

export const useTeamAccess = () => React.useContext(TeamAccessContext);

/** Delay before the single automatic retry after a failed lookup. */
const RETRY_DELAY_MS = 4000;

/**
 * Narrows an untrusted wire value. An unrecognised grant is treated as `denied`, not `unknown`:
 * `unknown` is a claim the server makes deliberately, and inferring it from a value we failed to
 * parse would manufacture doubt the server never expressed.
 */
const grant = (value: unknown): CatalogueGrant =>
  value === 'granted' || value === 'unknown' ? value : 'denied';

export const TeamAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [access, setAccess] = React.useState<TeamAccess>(NO_ACCESS);
  const reqRef = React.useRef(0);
  const inFlightRef = React.useRef(false);
  const pendingRef = React.useRef(false);
  const retryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors of state/props read from long-lived callbacks, so the listener below does not have to
  // be torn down and re-registered (and cannot capture a stale value) every time they change.
  const resolvedRef = React.useRef(false);
  const degradedRef = React.useRef(false);
  const userRef = React.useRef(user);
  userRef.current = user;
  const identityKey = getAccessIdentityKey(user);
  resolvedRef.current = access.resolved;
  const degraded = access.canTriage === 'unknown' || access.canManage === 'unknown';
  degradedRef.current = degraded;

  const load = React.useCallback(
    (allowRetry: boolean) => {
      if (inFlightRef.current) {
        // Someone asked while a request was already out. That request may be for a superseded
        // user, so remember to re-ask rather than dropping the request on the floor.
        pendingRef.current = true;
        return;
      }
      inFlightRef.current = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      const token = ++reqRef.current;
      void getMyTeamAccess()
        .then((res) => {
          if (token !== reqRef.current) return;
          // Trust the server's answer verbatim, including "unknown". Coercing it here — to either
          // "granted" or "denied" — would throw away the one thing this endpoint learned.
          setAccess({ canTriage: grant(res.canTriage), canManage: grant(res.canManage), resolved: true });
        })
        .catch(() => {
          if (token !== reqRef.current) return;
          // Stay closed, but give a transient failure exactly one more chance.
          setAccess(NO_ACCESS);
          if (allowRetry) {
            retryTimerRef.current = setTimeout(() => load(false), RETRY_DELAY_MS);
          }
        })
        .finally(() => {
          // Always clear, even when this response was superseded — otherwise a request that is
          // still in flight when the user changes would wedge the provider shut permanently.
          inFlightRef.current = false;
          if (pendingRef.current) {
            pendingRef.current = false;
            if (userRef.current) load(true);
          }
        });
    },
    [],
  );

  // Only meaningful once the session is established; AuthProvider redirects if it is not.
  //
  // Keyed on WHO the user is, not on the object describing them. `/api/auth/user` returns a fresh
  // object on every focus reverification, so depending on `user` made this effect fire on every
  // tab switch: it blanked the nav to NO_ACCESS and re-fetched an answer that had not changed,
  // which users saw as the Intake Management section collapsing and reappearing each time they
  // clicked back into the tab. The key is a primitive covering the account and its effective
  // roles, so it changes when the answer could change and not merely when the object is replaced.
  React.useEffect(() => {
    // Any change of identity — including one authenticated user to another — closes the nav
    // immediately and invalidates whatever is in flight or queued for the previous user, so their
    // flags can never be shown to, or resolved against, the new one.
    reqRef.current++;
    pendingRef.current = false;
    setAccess(NO_ACCESS);
    if (!userRef.current) return;
    load(true);
  }, [identityKey, load]);

  // Self-heal while we have never had an answer, and also while an answer we did get is
  // unverified. An established user must not be flipped back to "no access" by a blip on a focus
  // refetch, and re-asking on every focus would cost a request each time for a value that changes
  // about as often as team membership does — but an `unknown` is a transient claim, and without
  // this the spurious nav entries it produces would persist for the rest of the session even
  // after the outage ends.
  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !userRef.current) return;
      if (!resolvedRef.current || degradedRef.current) load(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [load]);

  React.useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    [],
  );

  const value = React.useMemo<TeamAccessContextValue>(
    () => ({ ...access, degraded, refresh: () => load(true) }),
    [access, degraded, load],
  );

  return <TeamAccessContext.Provider value={value}>{children}</TeamAccessContext.Provider>;
};

export default TeamAccessContext;
